import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { sendApiRequest } from '../lib/api/public';
import { useSession } from './SessionContext';
import { readFile } from '../lib/api/public';

type RecentlyViewedItem = {
  _id: string;
  firstName?: string;
  lastName?: string;
  primaryEmail?: string;
  avatarUrl?: string;
  title?: string;
  clientName?: string;
  clientId?: string;
  name?: string;
  logo?: string;
  lastViewedAt?: string;
};

type RecentlyViewedData = {
  candidates: RecentlyViewedItem[];
  jobs: RecentlyViewedItem[];
  clients: RecentlyViewedItem[];
};

const STORAGE_KEY = 'recentlyViewedDrawerOpen';

const RecentlyViewed = () => {
  const router = useRouter();
  const { user } = useSession();
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    return storedValue === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'candidates' | 'jobs' | 'clients'>('candidates');
  const [data, setData] = useState<RecentlyViewedData>({
    candidates: [],
    jobs: [],
    clients: [],
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, open ? 'true' : 'false');
  }, [open]);

  useEffect(() => {
    if (open && user) {
      void fetchRecentlyViewed();
    }
  }, [open, user]);

  const fetchRecentlyViewed = async () => {
    setLoading(true);

    try {
      const response = (await sendApiRequest({
        path: '/users/recently-viewed',
        method: 'GET',
      })) as RecentlyViewedData;

      setData(response);
    } catch (error) {
      console.error('Failed to fetch recently viewed items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const newOpen = !open;
    setOpen(newOpen);
  };

  const handleItemClick = (type: 'candidate' | 'job' | 'client', id: string) => {
    void router.push(`/${type}?id=${id}`);
  };

  const renderCandidateItem = (candidate: RecentlyViewedItem) => {
    const name =
      candidate.firstName && candidate.lastName
        ? `${candidate.firstName} ${candidate.lastName}`
        : candidate.primaryEmail || 'Unnamed';

    return (
      <ListItem key={candidate._id} disablePadding>
        <ListItemButton onClick={() => handleItemClick('candidate', candidate._id)} sx={{ py: 0.5 }}>
          <ListItemAvatar sx={{ minWidth: 40 }}>
            <Avatar
              src={candidate.avatarUrl ? readFile(candidate.avatarUrl) : undefined}
              sx={{ width: 32, height: 32 }}
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>
          </ListItemAvatar>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" noWrap>
              {name}
            </Typography>
            {candidate.primaryEmail && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {candidate.primaryEmail}
              </Typography>
            )}
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };

  const renderJobItem = (job: RecentlyViewedItem) => {
    return (
      <ListItem key={job._id} disablePadding>
        <ListItemButton onClick={() => handleItemClick('job', job._id)} sx={{ py: 0.5 }}>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" noWrap>
              {job.title || 'Untitled Job'}
            </Typography>
            {job.clientName && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {job.clientName}
              </Typography>
            )}
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };

  const renderClientItem = (client: RecentlyViewedItem) => {
    return (
      <ListItem key={client._id} disablePadding>
        <ListItemButton onClick={() => handleItemClick('client', client._id)} sx={{ py: 0.5 }}>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" noWrap>
              {client.name || 'Unnamed Client'}
            </Typography>
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };

  const getActiveItems = () => {
    return data[activeFilter] || [];
  };

  const renderActiveItems = () => {
    const items = getActiveItems();
    if (items.length === 0) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No items
          </Typography>
        </Box>
      );
    }

    return (
      <List dense sx={{ py: 0 }}>
        {items.map(item => {
          if (activeFilter === 'candidates') return renderCandidateItem(item);
          if (activeFilter === 'jobs') return renderJobItem(item);
          return renderClientItem(item);
        })}
      </List>
    );
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleToggle}
        ModalProps={{
          disableScrollLock: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          hideBackdrop: true,
          slotProps: {
            root: {
              style: { pointerEvents: 'none' },
            },
          },
        }}
        PaperProps={{
          sx: {
            width: 400,
            height: '300px',
            left: 'auto',
            right: 0,
            borderRadius: '8px 0 0 0',
            pointerEvents: 'auto',
          },
        }}
      >
        <Box sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle1">Recently Viewed</Typography>
            <IconButton onClick={handleToggle} size="small">
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
            <Chip
              label="Candidates"
              size="small"
              onClick={() => setActiveFilter('candidates')}
              color={activeFilter === 'candidates' ? 'secondary' : 'default'}
              variant={activeFilter === 'candidates' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Jobs"
              size="small"
              onClick={() => setActiveFilter('jobs')}
              color={activeFilter === 'jobs' ? 'secondary' : 'default'}
              variant={activeFilter === 'jobs' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Clients"
              size="small"
              onClick={() => setActiveFilter('clients')}
              color={activeFilter === 'clients' ? 'secondary' : 'default'}
              variant={activeFilter === 'clients' ? 'filled' : 'outlined'}
            />
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress color="secondary" size={32} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto' }}>{renderActiveItems()}</Box>
          )}
        </Box>
      </Drawer>

      {!open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            zIndex: 1300,
          }}
        >
          <Button
            color="secondary"
            onClick={handleToggle}
            startIcon={<KeyboardArrowUpIcon />}
            variant="contained"
            sx={{
              borderRadius: '8px 0 0 0',
              boxShadow: 4,
              margin: 0,
              '&:hover': {
                boxShadow: 6,
              },
            }}
          >
            Recently Viewed
          </Button>
        </Box>
      )}
    </>
  );
};

export default RecentlyViewed;
